import {formEncodedApi} from 'nutshell-core/api';
import {mapEntityToDisplayOption} from 'nutshell-core/utils';
import type {Entity, DisplayOptionEntity} from 'nutshell-core/utils';

import type {ParentChildFragment} from 'nutshell-graphql-types';

export type AccountInput = {
    name: string;
    description: string | null | undefined;
    address: string | null | undefined;
};

const ROWS = 7;

export function getPotentialAccounts(input: {
    accountInput: AccountInput;
    isAddingParent: boolean;
    accountId: string;
    alreadyAddedAccounts: string[];
}): Promise<any> {
    const {accountInput, isAddingParent, accountId, alreadyAddedAccounts} = input;

    const data = {
        rows: ROWS,
        fields: [
            {
                name: 'name',
                value: accountInput.name,
            },
            {
                name: 'description',
                value: accountInput.description,
            },
            {
                name: 'regions',
                value: accountInput.address,
            },
        ],
    } as const;

    return (
        formEncodedApi
            .post('/solr/find-potential-accounts.json', {
                ...data,
            })
            // @ts-expect-error
            .then((response) => response.json())
            .then((response: {results: Entity[]}) => {
                const results: Entity[] = response.results ? response.results : [];

                return results
                    .map((entity: Entity) => {
                        return mapEntityToDisplayOption(entity);
                    })
                    .map((entity: DisplayOptionEntity) => {
                        return {
                            ...entity,
                            // @ts-expect-error
                            ...getDisabledValues(entity, isAddingParent, accountId),
                        };
                    })
                    .sort((entity: DisplayOptionEntity) => {
                        return entity.isDisabled ? 1 : -1;
                    })
                    .filter((entity: DisplayOptionEntity) => {
                        return !alreadyAddedAccounts.includes(entity.id);
                    });
            })
    );
}

export const getDisabledValues = (
    entity: {
        id: string;
        parentAccountId: string | null | undefined;
        childAccountIds: string[] | null | undefined;
    },
    isAddingParentAccount: boolean,
    accountId: string
): any => {
    const isAChild = Boolean(entity.parentAccountId);
    const isAParent = Boolean(entity.childAccountIds && entity.childAccountIds.length > 0);
    const isAParentOrChild = isAParent || isAChild;

    const isDisabled =
        entity.id === accountId ||
        (isAddingParentAccount && isAChild) ||
        (!isAddingParentAccount && isAParentOrChild);

    const getDisabledText = () => {
        if (isDisabled) {
            if (entity.id === accountId) {
                return 'this company';
            } else {
                return `already a ${isAParent ? 'parent' : 'child'}`;
            }
        }
    };

    return {isDisabled, disabledText: getDisabledText()};
};

export const getAccountHierarchySectionHeader = (hasParent: boolean, hasChild: boolean): string => {
    let sectionHeader = 'Company hierarchy';
    if (hasParent) {
        sectionHeader = 'Parent company';
    } else if (hasChild) {
        sectionHeader = 'Child companies';
    }

    return sectionHeader;
};

export const getAccountCardSecondaryInfo = (account: ParentChildFragment) => {
    if (account.primaryContact) {
        return account.primaryContact.name;
    } else if (account.description) {
        return account.description;
    } else if (account.accountType) {
        return account.accountType.name;
    } else {
        return '';
    }
};

export const updatePageToReflectAccountHierarchyRemoval = (
    currentAccountId: string,
    childAccountId: string
) => {
    // This indicates we are removing a parent
    if (currentAccountId === childAccountId) {
        const overlay = document.querySelector('.peep-subheader .avatar-container .child-overlay');
        if (overlay) {
            overlay.remove();
        }
    } else {
        // This indicates we are removing a child
        const childCountOverlay = document.querySelector(
            '.peep-subheader .avatar-container .child-count-overlay'
        );
        if (childCountOverlay) {
            const numberOfChildren = Number(childCountOverlay.textContent);
            if (numberOfChildren === 1) {
                childCountOverlay.remove();
                changeIconsToAccountDefault();
            } else {
                const newNumberOfChildren = numberOfChildren - 1;
                childCountOverlay.textContent = `${newNumberOfChildren}`;
            }
        }
    }
};

const changeIconsToAccountDefault = () => {
    const subheaderAvatar = document.querySelector('.peep-subheader .avatar');
    if (subheaderAvatar) {
        subheaderAvatar.classList.remove('parent-account-avatar');
        subheaderAvatar.classList.add('account-avatar');
    }

    const sidebarAvatar = document.querySelector('.peep-icon-container');
    if (sidebarAvatar) {
        sidebarAvatar.classList.remove('parent-account-avatar');
    }
};

export const updatePageToReflectAccountHierarchyAddition = (
    currentAccountId: string,
    parentAccountId?: string | null
) => {
    // This indicates we are adding a child
    if (currentAccountId === parentAccountId) {
        const childCountOverlay = document.querySelector(
            '.peep-subheader .avatar-container .child-count-overlay'
        );
        if (childCountOverlay) {
            const numberOfChildren = Number(childCountOverlay.textContent);
            const newNumberOfChildren = numberOfChildren + 1;
            childCountOverlay.textContent = `${newNumberOfChildren}`;
        } else {
            const newChildCountOverlay = createChildCountOverlay();
            const avatarContainer = document.querySelector('.peep-subheader .avatar-container');
            if (avatarContainer) {
                avatarContainer.appendChild(newChildCountOverlay);
            }
            changeIconsToParentAccount();
        }
    } else {
        // This indicates we are adding a parent
        const childOverlay = createChildOverlay();
        const avatarContainer = document.querySelector('.peep-subheader .avatar-container');
        if (avatarContainer) {
            avatarContainer.appendChild(childOverlay);
        }
    }
};

const createChildCountOverlay = () => {
    const childCountOverlay = document.createElement('div');
    childCountOverlay.classList.add('child-count-overlay');
    childCountOverlay.textContent = '1';

    return childCountOverlay;
};

const changeIconsToParentAccount = () => {
    const subheaderAvatar = document.querySelector('.peep-subheader .avatar');
    if (subheaderAvatar) {
        subheaderAvatar.classList.remove('account-avatar');
        subheaderAvatar.classList.add('parent-account-avatar');
    }

    const sidebarAvatar = document.querySelector('.peep-icon-container');
    if (sidebarAvatar) {
        sidebarAvatar.classList.add('parent-account-avatar');
    }
};

const createChildOverlay = () => {
    const childOverlayImage = document.createElement('img');
    childOverlayImage.src = '/include/images/icons/avatars/account-child-overlay.svg';
    childOverlayImage.width = 36;
    childOverlayImage.height = 36;

    const childOverlay = document.createElement('div');
    childOverlay.classList.add('child-overlay');
    childOverlay.appendChild(childOverlayImage);

    return childOverlay;
};
